import React from 'react'
import {
  TextField,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Box,
  Typography,
  SelectChangeEvent
} from '@mui/material'
import { states } from 'constants/states'
import { useTranslation } from 'react-i18next'

type AddressFieldsProps = {
  formValues: {
    addressLine1: string
    addressLine2?: string
    city: string
    state: string
    zipCode: string
  }
  errors: {
    addressLine1: string
    city: string
    state: string
    zipCode: string
  }
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  handleBlur: (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void
  handleSelectChange: (e: SelectChangeEvent<string>) => void
  size?: 'small' | 'medium'
}

const AddressFields: React.FC<AddressFieldsProps> = ({
  formValues,
  errors,
  handleInputChange,
  handleBlur,
  handleSelectChange,
  size = 'small'
}) => {
  const { t } = useTranslation()

  return (
    <>
      <TextField
        label={t('addressFields.addressLine1')}
        name='addressLine1'
        value={formValues.addressLine1}
        onChange={handleInputChange}
        onBlur={handleBlur}
        placeholder={t('addressFields.placeholders.required')}
        fullWidth
        margin='dense'
        variant='outlined'
        required
        error={Boolean(errors.addressLine1)}
        helperText={
          errors.addressLine1 ? t('addressFields.errors.addressRequired') : ''
        }
        inputProps={{ maxLength: 50 }}
        size={size as 'small' | 'medium'}
      />

      <TextField
        label={t('addressFields.addressLine2')}
        name='addressLine2'
        value={formValues.addressLine2}
        onChange={handleInputChange}
        placeholder={t('addressFields.placeholders.optional')}
        fullWidth
        margin='dense'
        variant='outlined'
        inputProps={{ maxLength: 50 }}
        size={size as 'small' | 'medium'}
      />

      <Box display='flex' gap={2}>
        <TextField
          label={t('addressFields.city')}
          name='city'
          value={formValues.city}
          onChange={handleInputChange}
          onBlur={handleBlur}
          placeholder={t('addressFields.placeholders.required')}
          fullWidth
          margin='dense'
          variant='outlined'
          required
          error={Boolean(errors.city)}
          helperText={errors.city ? t('addressFields.errors.cityRequired') : ''}
          inputProps={{ maxLength: 50 }}
          size={size as 'small' | 'medium'}
        />

        <FormControl
          fullWidth
          margin='dense'
          required
          error={Boolean(errors.state)}
          size={size as 'small' | 'medium'}
        >
          <InputLabel>{t('addressFields.state')}</InputLabel>
          <Select
            label={t('addressFields.state')}
            name='state'
            value={formValues.state}
            onChange={handleSelectChange}
            onBlur={handleBlur}
          >
            {states.map(state => (
              <MenuItem key={state.value} value={state.value}>
                {state.label}
              </MenuItem>
            ))}
          </Select>
          <Typography
            variant='caption'
            sx={{
              mt: size === 'small' ? '4px' : '3px'
            }}
          >
            {errors.state ? t('addressFields.errors.stateRequired') : ''}
          </Typography>
        </FormControl>

        <TextField
          label={t('addressFields.zipCode')}
          name='zipCode'
          value={formValues.zipCode}
          onChange={handleInputChange}
          onBlur={handleBlur}
          placeholder={t('addressFields.placeholders.required')}
          fullWidth
          margin='dense'
          variant='outlined'
          required
          error={Boolean(errors.zipCode)}
          helperText={
            errors.zipCode ? t('addressFields.errors.zipCodeFormat') : ''
          }
          inputProps={{ maxLength: 10 }}
          size={size as 'small' | 'medium'}
        />
      </Box>
    </>
  )
}

export default AddressFields
